import { useQuery } from "@tanstack/react-query";
import { ClipboardList } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { isMissingRelation } from "@/stores/pm/repository";
import { briefKeys } from "../queryKeys";
import { fetchBriefOriginForIssue } from "../repository";

interface BriefOriginTagProps {
  issueId: number;
}

/** Before the appendix-issue migration applies the column is missing, not the table. */
function isMissingSchema(error: unknown): boolean {
  const code = (error as { code?: string } | null)?.code;
  return isMissingRelation({ code }) || code === "42703";
}

/**
 * On an issue created from a brief appendix or from one of Franklin's
 * suggestions: a tag naming it, linking to it on the Brief page. Outlined rather than filled so it does not
 * read as the grey parent-issue tag. Renders nothing when there is no origin,
 * when the viewer cannot see the brief (row-level security hides the row), or
 * before the brief schema exists.
 */
export function BriefOriginTag({ issueId }: BriefOriginTagProps) {
  const { t } = useLanguage();
  const copy = t.brief.issueOrigin;
  const { data: origin } = useQuery({
    queryKey: briefKeys.origin(issueId),
    queryFn: async () => {
      try {
        return await fetchBriefOriginForIssue(issueId);
      } catch (error) {
        if (isMissingSchema(error)) return null;
        throw error;
      }
    },
    staleTime: 60_000,
  });

  if (!origin) return null;

  const fromSuggestion = origin.kind === "suggestion";
  const target = fromSuggestion ? `finding=${origin.id}` : `appendix=${origin.id}`;
  return (
    <Link
      to={`/buildings/${origin.building_id}/brief?project=${origin.project_id}&${target}`}
      title={fromSuggestion ? copy.openSuggestion : copy.open}
      data-testid="brief-origin-tag"
      className="inline-flex min-w-0 max-w-[16rem] items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground hover:border-foreground/30 hover:text-foreground"
    >
      <ClipboardList aria-hidden="true" className="size-3 shrink-0" />
      <span className="shrink-0">{fromSuggestion ? copy.suggestionLabel : copy.label}</span>
      <span aria-hidden="true">·</span>
      <span className="truncate">{origin.title}</span>
    </Link>
  );
}
